'use client';

import { memo, useEffect, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';
import { Button } from '@/_kit/button';
import { EButtonColor, EButtonSize } from '@/_kit/button/_types';
import { ArrowLeftIcon } from '@/_kit/icons/arrow-left-icon';
import { ArrowRightIcon } from '@/_kit/icons/arrow-right-icon';
import { TCard } from '../../_types';
import { Card } from './_components/card';
import { END_NUMBER, START_NUMBER } from './_constants';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Slider';

type TProps = {
  cards: Array<TCard>;
  isStage: boolean;
};

export const Slider = memo(function Slider({ cards, isStage }: TProps) {
  const swiperRef = useRef<HTMLDivElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

  const sliderObserverRefCurrent = swiperRef.current;

  useEffect(() => {
    if (sliderObserverRefCurrent) {
      const observer = new IntersectionObserver(([entry]) => {
        setIsIntersecting(Boolean(entry?.isIntersecting));
      });

      observer.observe(sliderObserverRefCurrent);

      return () => observer.unobserve(sliderObserverRefCurrent);
    }
  }, [sliderObserverRefCurrent]);

  return (
    <div ref={swiperRef} className={cn(BLOCK_NAME)}>
      <Swiper
        centeredSlides={false}
        className={cn(`${BLOCK_NAME}__swiper`)}
        modules={[FreeMode, Navigation]}
        navigation={{
          nextEl: `.${BLOCK_NAME}__button--next`,
          prevEl: `.${BLOCK_NAME}__button--prev`,
        }}
        onTransitionEnd={(swiper) => {
          setIsEnd(swiper.progress > END_NUMBER);
          setIsBeginning(swiper.activeIndex === START_NUMBER);
        }}
        slidesPerView="auto"
        spaceBetween={16}
      >
        {cards.map((slideProps, index) => {
          return (
            <SwiperSlide
              key={index}
              className={cn(`${BLOCK_NAME}__swiper-slide`)}
            >
              <div className={cn(`${BLOCK_NAME}__swiper-slide-card`)}>
                <Card
                  isIntersectingSlider={isIntersecting}
                  isStage={isStage}
                  {...slideProps}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className={cn(`${BLOCK_NAME}__container`)}>
        <div className={cn(`${BLOCK_NAME}__buttons`)}>
          <div
            className={cn(
              `${BLOCK_NAME}__button`,
              `${BLOCK_NAME}__button--prev`,
            )}
          >
            <Button
              backgroundColor={EButtonColor.EMPTY}
              disabled={isBeginning}
              rightIcon={ArrowLeftIcon}
              size={EButtonSize.MEDIUM}
              strokeColor={EButtonColor.BLACK}
            />
          </div>
          <div
            className={cn(
              `${BLOCK_NAME}__button`,
              `${BLOCK_NAME}__button--next`,
            )}
          >
            <Button
              backgroundColor={EButtonColor.EMPTY}
              disabled={isEnd}
              rightIcon={ArrowRightIcon}
              size={EButtonSize.MEDIUM}
              strokeColor={EButtonColor.BLACK}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
